import {useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router';

import {dataTableActionT, dataTableColumnT} from '@/lib/types/dataTable.types';
import {SelectedColumnsT} from '@/lib/types/query.types';
import {rateSheetT} from '@/lib/types/rateSheets.types';
import {BasicDistributorT} from '@/lib/types/user.types';

import {createDataTableConfig} from '@/lib/helpers/config';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable, DataTableRefT} from '@/components/dataTable';

import DistributorSelector from './components/distributorSelector';

function DistributorRateSheetsPage() {
  // Hooks
  const navigate = useNavigate();
  const {
    isSpeculator,
    userAccounts: {distributors},
  } = useAuth();
  const {toast} = useNotifications();

  // State
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedRateSheet, setSelectedRateSheet] = useState<rateSheetT | null>(null);
  const [selectedDistributor, setSelectedDistributor] = useState<BasicDistributorT | null>(distributors?.[0] || null);

  // Refs
  const dataTableRef = useRef<DataTableRefT | null>(null);

  // Data Table -----------------------------------------------------
  const {table, title, columns, searchColumn, filters} = createDataTableConfig({
    table: 'Rate_Sheets',
    title: 'Rate Sheets',
    searchColumn: 'fk_distributor_id',
    columns: ['*', ['Distributor', ['*']], ['Market', ['*']]],
    filters: [
      // {column: 'fk_distributor_id', operator: 'eq', value: selectedDistributor?.distributor_id},
      {column: 'date_removed', operator: 'is', value: null},
    ],
    enabled: !!selectedDistributor?.distributor_id,
  });

  // Helper types to make the code cleaner and DRY
  type DataTableT = typeof table;
  type DataTableColumnsT = typeof columns;
  type DataTableRowT = SelectedColumnsT<DataTableT, DataTableColumnsT>;

  // Table Columns
  const columnsDisplay: dataTableColumnT<DataTableT, DataTableRowT>[] = [
    {
      header: 'Rate Sheet',
      field: 'rate_sheet_name',
      render: row => (
        <Link
          className="text-primary hover:text-primary dark:text-primary"
          to={`/distributor/rate-sheets/${row.rate_sheet_id}`}
        >
          {row.rate_sheet_name}
        </Link>
      ),
    },
    {
      header: 'Market',
      field: 'Market.market_name',
    },
    {
      header: 'Distributor',
      field: 'Distributor.distributor_name',
    },
    {
      header: 'Created',
      field: 'date_created',
      render: row => (
        <span>
          {new Date(row.date_created).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})}
        </span>
      ),
    },
    {
      header: 'Modified',
      field: 'date_modified',
      render: row => (
        <span>
          {new Date(row.date_modified).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})}
        </span>
      ),
    },
  ];

  // Table actions
  const actions: dataTableActionT<'Rate_Sheets', DataTableRowT>[] = [
    {
      icon: 'ri-pencil-line',
      color: 'info',
      handler: (row: rateSheetT) => navigate(`/distributor/rate-sheets/${row.rate_sheet_id}/edit`),
      permission: () => isSpeculator,
    },
    {
      icon: 'ri-delete-bin-line',
      color: 'danger',
      handler: (row: rateSheetT) => handleDeleteSelect(row),
      permission: () => isSpeculator,
    },
  ];

  /****************************************************
   * HANDLERS
   ****************************************************/
  /**
   * Handle Deleting a Rate Sheet
   */
  const handleDeleteRateSheet = async () => {
    if (!selectedRateSheet || isDeleting) return;

    const {data: supabaseUser} = await SupabaseInstance.supabase.auth.getUser();

    if (!supabaseUser) {
      return;
    }

    try {
      setIsDeleting(true);
      const {error} = await SupabaseInstance.supabase
        .from('Rate_Sheets')
        .update({date_removed: new Date().toISOString(), removed_by: supabaseUser.user?.id})
        .eq('rate_sheet_id', selectedRateSheet?.rate_sheet_id);

      if (error) throw error;

      toast.success('Rate sheet deleted successfully');
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error('Error deleting rate sheet:', error);
    } finally {
      setIsDeleting(false);
      dataTableRef.current?.refetch();
    }
  };

  const handleDeleteSelect = (row: rateSheetT) => {
    setSelectedRateSheet(row);
    setIsDeleteModalVisible(true);
  };

  return (
    <Page currentPage="Rate Sheets">
      {/* Distributor Selector */}
      <div className="mb-4">
        <DistributorSelector
          distributors={distributors || []}
          selectedDistributor={selectedDistributor}
          setSelectedDistributor={setSelectedDistributor}
        />
      </div>
      <DataTable
        ref={dataTableRef}
        title={title}
        table={table}
        columns={columns}
        filters={filters}
        searchColumn={searchColumn}
        columnsDisplay={columnsDisplay}
        actionsDisplay={actions}
        onAdd={() => navigate('/distributor/rate-sheets/new')}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDeleteRateSheet}
        title="Confirm Deletion"
        description="Are you sure you want to delete this rate sheet? This action cannot be undone."
        isSubmitting={isDeleting}
      />
    </Page>
  );
}

export default DistributorRateSheetsPage;
